"use client"

import { useState, useRef, useEffect } from "react"
import { Card, CardContent } from "@/components/ui/card"
import { ChevronDown, ChevronUp } from "lucide-react"
import { Review } from "@/types/reviews"
import { ReviewStars } from "@/components/pages/home/reviews/review-stars"
import { useTranslations } from "next-intl"

interface ReviewCardProps {
  review: Review
}

export function ReviewCard({ review }: ReviewCardProps) {
  const t = useTranslations("page.homepage.reviews.card")

  const [isExpanded, setIsExpanded] = useState(false)
  const [isTruncated, setIsTruncated] = useState(false)
  const textRef = useRef<HTMLParagraphElement>(null)

  useEffect(() => {
    if (textRef.current) {
      const element = textRef.current
      setIsTruncated(element.scrollHeight > element.clientHeight)
    }
  }, [review.text])

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
  }

  return (
    <Card className="border-teal-100 h-full">
      <CardContent className="p-6 flex flex-col h-full">
        <div className="mb-3">
          <ReviewStars rating={review.rating} />
        </div>
        <div className="relative flex-1">
          <p 
            ref={textRef}
            className={`text-gray-600 mb-4 text-sm leading-relaxed ${
              !isExpanded ? "line-clamp-5" : ""
            }`}
          >
            "{review.text}"
          </p>
          {isTruncated && (
            <button
              type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-teal-600 hover:text-teal-700 text-sm font-medium flex items-center gap-1"
              aria-expanded={isExpanded}
            >
              {isExpanded ? (
                <>
                  {t("show_less")}
                  <ChevronUp className="h-4 w-4" />
                </>
              ) : (
                <>
                  {t("show_more")}
                  <ChevronDown className="h-4 w-4" />
                </>
              )}
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 mt-4">
          <a 
            href={review.reviewLink} 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-2 group"
          >
            <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center group-hover:bg-teal-200 transition-colors">
              <span className="text-teal-600 font-semibold text-sm">{getInitials(review.author)}</span>
            </div>
            <div>
              <span className="font-medium text-sm text-gray-900 group-hover:text-teal-600 transition-colors">
                {review.author}
              </span>
              <div className="flex items-center gap-2">
                <p className="text-xs text-gray-500">{review.date}</p>
                <span className="text-xs text-gray-400">•</span>
                <p className="text-xs text-gray-500 capitalize">{review.platform}</p>
              </div>
            </div>
          </a>
        </div>
      </CardContent>
    </Card>
  )
} 
